import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowLeft, MapPin, Package, Printer, Trash2, Minus, Plus, X, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/book-in.css";
import "../styles/pallet-receiving.css";
import { useTranslation } from "../i18n";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import {
    playSuccess,
    playError,
    playBoxAdd,
    playComplete,
    playToteFinalised,
} from "../components/helpers/audioPlayer";
import {
    silentPrintBarcode,
    silentPrintZplRaw,
    silentPrint,
    buildPalletLabel,
    buildPalletZpl,
    inboundLabelName,
    browserPrint,
    isPrintAgentAvailable,
} from "../components/helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
    isSavedNetworkPrinterOnline,
} from "../components/helpers/networkPrint";
import HiddenScanInput from "../components/HiddenScanInput";
import { useDemoVisible } from "../components/helpers/demoMode";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import FitWord from "../components/FitWord";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import PrinterSelectModal from "../components/PrinterSelectModal";
import FlowLoader from "../components/FlowLoader";
import { apiFetch } from "../api/apiFetch";

const QUANTITY_MAX = 50;

// The supplier prompt always starts on this, and it is always offered even
// if the suppliers table does not (yet) carry it. Same rule as Goods In ›
// Pallets.
export const DEFAULT_SUPPLIER = "Amazon";

const TOAST_ID = {
    nothingScanned: "pr-nothing-scanned",
    palletUnknown: "pr-pallet-unknown",
    palletWrongWarehouse: "pr-pallet-wrong-warehouse",
    palletAlreadyPlaced: "pr-pallet-already-placed",
    invalidLocation: "pr-invalid-location",
    loadingBayOnly: "pr-loading-bay-only",
    serverError: "pr-server-error",
    palletVoided: "pr-pallet-voided",
    cannotVoidPlaced: "pr-cannot-void-placed",
    printFailed: "pr-print-failed",
    printSuccess: "pr-print-success",
} as const;

type Phase =
    | "select-quantity"
    | "awaiting-pallet"
    | "awaiting-location"
    | "removing"
    | "complete";

interface PendingPallet {
    pallet_id: string;
    daily_seq: number;
    created_at: string;
    stock_type?: string | null;
    supplier_name?: string | null;
}

// Inbound stock type from Goods In settings — code is the barcode prefix,
// name is printed on the label. Empty list = legacy 'P' prefix, no prompt.
interface StockType {
    name: string;
    code: string;
}

const PalletReceiving = () => {
    usePageTitle("pallet_receiving");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const demo = useDemoVisible();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };

    const [phase, setPhase] = useState<Phase>("select-quantity");
    const [quantity, setQuantity] = useState(1);
    const [todayCount, setTodayCount] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const [pendingList, setPendingList] = useState<PendingPallet[]>([]);
    // Holds the page in a neutral loading state until the first /pending
    // poll resolves. Without this the user sees a flash of the print
    // (select-quantity) UI before being auto-resumed into scan mode.
    const [initialised, setInitialised] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [currentPallet, setCurrentPallet] = useState<string | null>(null);
    // Stock types configured for this warehouse (Goods In settings). When
    // non-empty, Print Labels opens a picker first and the chosen type's
    // code prefixes every barcode in the batch.
    const [stockTypes, setStockTypes] = useState<StockType[]>([]);
    // Print prompt — always opens on Print Labels: the operator confirms
    // the supplier (dropdown, Amazon preselected) and, when the warehouse
    // has stock types, taps one to print; otherwise a single Print button.
    const [printPromptOpen, setPrintPromptOpen] = useState(false);
    const [suppliers, setSuppliers] = useState<string[]>([DEFAULT_SUPPLIER]);
    const [supplier, setSupplier] = useState(DEFAULT_SUPPLIER);
    // Demo barcodes — real loading-bay locations for this warehouse, fetched
    // once on mount so demo buttons always hit valid DB rows.
    const [demoLoadingBays, setDemoLoadingBays] = useState<string[]>([]);
    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error" | "complete">("idle");

    // Printer configuration — mirrors the scansheet pattern. The HiddenScanInput's
    // built-in print toggle (bottom-left fixed-position icon) shows green/red
    // based on `printConnected`, and clicking it opens the PrinterSelectModal
    // (which itself handles the install-Print-Agent prompt for first-time users).
    const [printMode, setPrintMode] = useState<"local" | "network" | null>(null);
    const [printerOnline, setPrinterOnline] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);

    const phaseRef = useRef(phase);
    const currentPalletRef = useRef(currentPallet);
    const printingRef = useRef(false);
    useEffect(() => { phaseRef.current = phase; }, [phase]);
    useEffect(() => { currentPalletRef.current = currentPallet; }, [currentPallet]);

    // Detect printer availability once on mount (and again any time the modal
    // closes, in case the user just installed/connected a printer).
    const detectPrinter = useCallback(async () => {
        // Network printer first (saved in localStorage).
        if (getSavedNetworkPrinter()) {
            const online = await isSavedNetworkPrinterOnline();
            if (online) {
                setPrintMode("network");
                setPrinterOnline(true);
                return;
            }
        }
        // Local Zebra agent.
        const localOk = await isPrintAgentAvailable();
        if (localOk) {
            setPrintMode("local");
            setPrinterOnline(true);
            return;
        }
        setPrintMode(null);
        setPrinterOnline(false);
    }, []);

    useEffect(() => {
        detectPrinter();
    }, [detectPrinter]);

    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
        abortRef.current?.abort();
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    }, []);

    const getSignal = () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        return ctrl.signal;
    };

    const flashStatus = (status: "success" | "error" | "complete") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), 800);
    };

    // Fetch counts + check for pending pallets to auto-resume into the
    // placement loop. Operators come back to where they left off.
    const refreshCountsAndResume = useCallback(async (autoResume: boolean) => {
        const wh = userObject.warehouse;
        if (!wh) {
            setInitialised(true);
            return;
        }
        try {
            const [countRes, pendingRes] = await Promise.all([
                apiFetch(`/pallet-receiving/today-count?warehouse=${encodeURIComponent(wh)}`, {
                }),
                apiFetch(`/pallet-receiving/pending?warehouse=${encodeURIComponent(wh)}`, {
                }),
            ]);
            if (!mountedRef.current) return;
            if (countRes.ok) {
                const data = await countRes.json();
                if (typeof data.todayCount === "number") setTodayCount(data.todayCount);
                if (typeof data.pendingCount === "number") setPendingCount(data.pendingCount);
            }
            if (pendingRes.ok) {
                const data = await pendingRes.json();
                if (Array.isArray(data.pallets)) setPendingList(data.pallets);
                if (autoResume && data.count > 0 && phaseRef.current === "select-quantity") {
                    setPhase("awaiting-pallet");
                }
            }
        } catch {
            // Stay at default state.
        } finally {
            if (mountedRef.current) setInitialised(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userObject.warehouse]);

    useEffect(() => {
        refreshCountsAndResume(true);
    }, [refreshCountsAndResume]);

    // Fetch real storage locations once on mount so the demo buttons hit
    // valid DB rows. Any physical location is a valid placement target
    // (lane, storage, pallet spot …) — only the scan-sheet area and the
    // virtual in-transit row are not, mirroring the BE rule.
    useEffect(() => {
        if (!userObject.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/put-away/locations`, {});
                if (!res.ok || !alive) return;
                const data: Array<{ barcode: string; warehouse: string; type?: string }> = await res.json();
                const barcodes = data
                    .filter(l => l.warehouse === userObject.warehouse && l.type !== "area" && !/^IN-TRANSIT/i.test(l.barcode))
                    .map(l => l.barcode)
                    .slice(0, 4);
                if (alive) setDemoLoadingBays(barcodes);
            } catch {
                // demo simply hides if there are none
            }
        })();
        return () => { alive = false; };
    }, [userObject.warehouse]);

    // Inbound stock types for this warehouse — fetched once on mount. A
    // failed fetch leaves the list empty, which prints straight away with
    // the legacy prefix; the BE still rejects a type-less print for a
    // warehouse that has types, so nothing mislabelled can slip through.
    useEffect(() => {
        if (!userObject.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/pallet-receiving/stock-types?warehouse=${encodeURIComponent(userObject.warehouse)}`, {});
                if (!res.ok || !alive) return;
                const data: { types?: StockType[] } = await res.json();
                if (alive && Array.isArray(data.types)) setStockTypes(data.types);
            } catch {
                // leave empty
            }
        })();
        return () => { alive = false; };
    }, [userObject.warehouse]);

    // Supplier dropdown: every active supplier from the canonical
    // /suppliers table (same source as Products / Pallets), with Amazon
    // guaranteed to be present since it is the default. A failed fetch
    // leaves the default-only list, which still prints.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/suppliers?status=active&page=0&page_size=500`, {});
                if (!res.ok || !alive) return;
                const data = await res.json().catch(() => null);
                const names: string[] = Array.isArray(data?.suppliers)
                    ? data.suppliers.map((s: { name?: string }) => s.name).filter((n: unknown): n is string => typeof n === "string" && n.trim() !== "")
                    : [];
                if (!names.includes(DEFAULT_SUPPLIER)) names.push(DEFAULT_SUPPLIER);
                names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
                if (alive) setSuppliers(names);
            } catch {
                // keep the default-only list
            }
        })();
        return () => { alive = false; };
    }, []);

    // Print a single label, preferring network → local agent → browser print.
    const printOne = async (palletId: string, opts: {
        warehouse: string;
        createdAt: string;
        batchIndex: number;
        batchTotal: number;
        dailySeq: number;
        operator?: string;
        stockType?: string;
        supplier?: string;
    }): Promise<{ success: boolean; error?: string }> => {
        const labelHtml = buildPalletLabel(palletId, opts);
        const labelZpl = buildPalletZpl(palletId, opts);
        // Stock type / supplier line for the LOCAL pre-1.6.0 fallback below.
        const name = inboundLabelName(opts.stockType, opts.supplier);

        // Network first if a printer is saved (mirrors pallets.tsx). The job
        // carries BOTH renderings of the design: the raw ZPL for a Zebra
        // (printed verbatim — a headless Pi agent has no HTML renderer at
        // all) and the HTML for any other printer, via its driver. No
        // barcodeValue: that would select the agent's generic 65×38 layout,
        // which is not the design and clipped 19-char pallet IDs.
        const saved = getSavedNetworkPrinter();
        if (saved) {
            const result = await submitNetworkPrintJob(
                saved.agentId,
                saved.printerName,
                labelHtml,
                1,
                null,
                false,
                { zpl: labelZpl },
            );
            if (result.success) return { success: true };
            // Fall through to local on network failure.
        }

        // Local Zebra: the full 4×6 label as raw ZPL (agent ≥ 1.6.0) —
        // logo, warehouse, stock type, supplier, wide barcode, counters.
        const zebraRaw = await silentPrintZplRaw(labelZpl, 1);
        if (zebraRaw.success) return { success: true };

        // Older agents (< 1.6.0) can't take raw ZPL: fall back to their
        // generic barcode layout with the stock type / supplier as the
        // name line. Non-Zebra printers get the HTML from this call.
        const zebra = await silentPrintBarcode(palletId, 1, {
            ...(name ? { name } : {}),
            fallbackHtml: labelHtml,
        });
        if (zebra.success) return { success: true };

        // Local non-Zebra HTML fallback.
        const local = await silentPrint(labelHtml);
        if (local.success) return { success: true };

        // Last resort: browser print dialog.
        const browser = await browserPrint(labelHtml);
        return browser;
    };

    // Print runs entirely in the background — no phase transitions, no UI
    // reset. The operator stays exactly where they are (typing into the
    // stepper, scanning a pallet, etc); only the print button itself shows
    // a spinning state, plus a single toast confirms the result.
    const handleGenerateAndPrint = async (stockType?: StockType) => {
        if (!userObject.warehouse) return;
        if (printingRef.current) return;        // ignore double-taps
        const supplierName = supplier.trim();
        if (!supplierName) {
            toast.error(t("inbound_supplier_required"), { toastId: TOAST_ID.serverError });
            return;
        }
        printingRef.current = true;
        setIsPrinting(true);
        setPrintPromptOpen(false);
        try {
            const res = await apiFetch(`/pallet-receiving/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    count: quantity,
                    uid: userObject.uid,
                    name: userObject.name,
                    warehouse: userObject.warehouse,
                    ...(stockType ? { stockType: stockType.code } : {}),
                    supplierName,
                }),
                signal: getSignal(),
            });
            if (res.status === 401) {
                logoutProcessor();
                navigate("/login");
                return;
            }
            if (!res.ok) {
                playError();
                // 400 with a stock-type reason means settings changed under
                // us (types added after mount, or the chosen one removed).
                // Refresh the local list so the next tap prompts correctly.
                const body = await res.json().catch(() => null) as { reason?: string; types?: StockType[] } | null;
                if (body?.reason === "stock_type_required" || body?.reason === "stock_type_unknown") {
                    if (Array.isArray(body.types)) setStockTypes(body.types);
                    toast.error(
                        t(body.reason === "stock_type_required" ? "inbound_stock_type_required" : "inbound_stock_type_unknown"),
                        { toastId: TOAST_ID.serverError },
                    );
                    return;
                }
                if (body?.reason === "supplier_required") {
                    toast.error(t("inbound_supplier_required"), { toastId: TOAST_ID.serverError });
                    return;
                }
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            const data = await res.json();
            const pallets: PendingPallet[] = Array.isArray(data.pallets) ? data.pallets : [];
            if (pallets.length === 0) return;

            let printFailures = 0;
            for (let i = 0; i < pallets.length; i++) {
                if (!mountedRef.current) return;
                const p = pallets[i];
                const result = await printOne(p.pallet_id, {
                    warehouse: userObject.warehouse,
                    createdAt: p.created_at,
                    batchIndex: i + 1,
                    batchTotal: pallets.length,
                    dailySeq: p.daily_seq,
                    operator: userObject.name,
                    stockType: p.stock_type ?? stockType?.name,
                    supplier: p.supplier_name ?? supplierName,
                });
                if (!result.success) printFailures += 1;
            }

            if (printFailures > 0) {
                toast.warn(
                    t("print_partial_failed", { failed: String(printFailures), total: String(pallets.length) }),
                    { toastId: TOAST_ID.printFailed },
                );
            } else {
                playBoxAdd();
                toast.success(
                    t("printed_n_labels", { count: String(pallets.length) }),
                    { toastId: TOAST_ID.printSuccess },
                );
            }

            await refreshCountsAndResume(false);
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            printingRef.current = false;
            if (mountedRef.current) setIsPrinting(false);
        }
    };

    const checkPallet = async (palletId: string) => {
        const res = await apiFetch(`/pallet-receiving/pallet/check`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ palletId, warehouse: userObject.warehouse }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { valid: false } as { valid: boolean; reason?: string };
        }
        if (!res.ok) return { valid: false };
        return await res.json();
    };

    const checkLocation = async (locationId: string) => {
        const res = await apiFetch(`/pallet-receiving/location/check`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locationId, warehouse: userObject.warehouse }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { valid: false } as { valid: boolean; reason?: string };
        }
        if (!res.ok) return { valid: false };
        return await res.json();
    };

    const placePallet = async (palletId: string, locationId: string) => {
        const res = await apiFetch(`/pallet-receiving/place`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                palletId,
                locationId,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return null;
        }
        if (!res.ok) return null;
        return await res.json() as { success: boolean; pendingRemaining: number };
    };

    const voidPallet = async (palletId: string) => {
        const res = await apiFetch(`/pallet-receiving/void`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                palletId,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return null;
        }
        const data = await res.json().catch(() => null);
        return data as { success: boolean; reason?: string } | null;
    };

    const finishIfDone = (pendingRemaining: number) => {
        if (pendingRemaining === 0) {
            playComplete();
            setPhase("complete");
            setCurrentPallet(null);
        }
    };

    const doSubmit = async (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        if (!val) {
            toast.error(t("nothing_scanned"), { toastId: TOAST_ID.nothingScanned });
            return;
        }
        setLoading(true);
        try {
            const phase = phaseRef.current;

            if (phase === "removing") {
                const result = await voidPallet(val);
                if (!result) {
                    playError();
                    flashStatus("error");
                    toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                    return;
                }
                if (!result.success) {
                    playError();
                    flashStatus("error");
                    if (result.reason === "cannot_void_placed") {
                        toast.error(t("cannot_void_placed"), { toastId: TOAST_ID.cannotVoidPlaced });
                    } else if (result.reason === "pallet_unknown") {
                        toast.error(t("pallet_unknown"), { toastId: TOAST_ID.palletUnknown });
                    } else {
                        toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                    }
                    return;
                }
                playSuccess();
                flashStatus("success");
                toast.info(t("pallet_voided"), { toastId: TOAST_ID.palletVoided });
                await refreshCountsAndResume(false);
                return;
            }

            // select-quantity AND awaiting-pallet share the scan handler:
            // scanning a pending label from the print page jumps straight to
            // the SCAN LOADING BAY phase without an extra "Scan to place"
            // tap. The check covers warehouse + status, so unknown / placed
            // / wrong-warehouse barcodes still fall back to a clean error.
            if (phase === "awaiting-pallet" || phase === "select-quantity") {
                const result = await checkPallet(val);
                if (!result.valid) {
                    playError();
                    flashStatus("error");
                    const msg = result.reason === "pallet_wrong_warehouse"
                        ? t("pallet_wrong_warehouse")
                        : result.reason === "pallet_already_placed"
                            ? t("pallet_already_placed")
                            : t("pallet_unknown");
                    const id = result.reason === "pallet_wrong_warehouse"
                        ? TOAST_ID.palletWrongWarehouse
                        : result.reason === "pallet_already_placed"
                            ? TOAST_ID.palletAlreadyPlaced
                            : TOAST_ID.palletUnknown;
                    toast.error(msg, { toastId: id });
                    return;
                }
                setCurrentPallet(val);
                setPhase("awaiting-location");
                playBoxAdd();
                flashStatus("success");
                return;
            }

            if (phase === "awaiting-location") {
                // Rescanning the same pallet cancels the selection.
                if (val === currentPalletRef.current) {
                    setCurrentPallet(null);
                    setPhase("awaiting-pallet");
                    playBoxAdd();
                    flashStatus("success");
                    return;
                }

                const loc = await checkLocation(val);
                if (!loc.valid) {
                    playError();
                    flashStatus("error");
                    if (loc.reason === "wrong_type") {
                        toast.error(t("pallet_loading_bay_only"), { toastId: TOAST_ID.loadingBayOnly });
                    } else {
                        toast.error(t("invalid_location"), { toastId: TOAST_ID.invalidLocation });
                    }
                    return;
                }

                const placed = await placePallet(currentPalletRef.current!, val);
                if (!placed?.success) {
                    playError();
                    flashStatus("error");
                    toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                    return;
                }
                playToteFinalised();
                flashStatus("complete");
                setCurrentPallet(null);
                setPendingCount(placed.pendingRemaining);
                if (placed.pendingRemaining === 0) {
                    finishIfDone(0);
                } else {
                    setPhase("awaiting-pallet");
                }
                return;
            }
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                setScanValue("");
            }
        }
    };

    const toggleRemoving = () => {
        if (phase === "removing") {
            // Returning from remove mode — drop back to whatever placement
            // phase the user came from (awaiting-pallet is always safe).
            setPhase(pendingCount > 0 ? "awaiting-pallet" : "select-quantity");
        } else {
            setPhase("removing");
            setCurrentPallet(null);
        }
    };

    const startReceiveMore = () => {
        setPhase("select-quantity");
        setQuantity(1);
    };

    // Manual void via the X icon on the in-progress list. Mirrors the scan
    // path but skips the lookup since we already have the pallet IDs in
    // pendingList.
    const handleManualVoid = async (palletId: string) => {
        const result = await voidPallet(palletId);
        if (!result) {
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            return;
        }
        if (!result.success) {
            playError();
            const msg = result.reason === "cannot_void_placed"
                ? t("cannot_void_placed")
                : t("server_error");
            const id = result.reason === "cannot_void_placed"
                ? TOAST_ID.cannotVoidPlaced
                : TOAST_ID.serverError;
            toast.error(msg, { toastId: id });
            return;
        }
        playSuccess();
        toast.info(t("pallet_voided"), { toastId: TOAST_ID.palletVoided });
        await refreshCountsAndResume(false);
    };

    // Stock type of the pallet currently being placed — looked up in the
    // pending list (every placeable pallet is pending, and the list is
    // refreshed after each print/void) so no extra state to keep in sync.
    const currentPalletType = currentPallet
        ? pendingList.find(p => p.pallet_id === currentPallet)?.stock_type ?? null
        : null;

    // Command word(s) for the centre of the screen.
    const commandText = (() => {
        switch (phase) {
            case "select-quantity": return t("how_many_labels");
            case "awaiting-pallet": return t("scan_pallet");
            case "awaiting-location": return t("scan_loading_bay");
            case "removing": return t("scan_label_to_void");
            case "complete": return t("pallets_all_placed");
        }
    })();
    const commandWords = commandText.toUpperCase().split(/[\s-]+/).filter(Boolean);

    return (
        <div
            className="scan-panel pallet-receiving-panel"
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
                <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("pallet_receiving")}</h1>
                <button className="page-back-btn" onClick={() => navigate("/goods-in")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {!initialised && (
                <div className="pallet-receiving-initial-loader">
                    <FlowLoader />
                </div>
            )}

            {initialised && phase !== "complete" && (
                <div className="book-in-stats-bar">
                    {currentPallet ? (
                        <span className="book-in-stat">
                            <Package size={16} />
                            <strong>{currentPallet}</strong>
                            {currentPalletType && (
                                <span className="pallet-receiving-type-tag">{currentPalletType}</span>
                            )}
                        </span>
                    ) : (
                        <span className="book-in-stat">
                            <MapPin size={16} />
                            <strong>{t("pallets_pending_count", { count: String(pendingCount) })}</strong>
                        </span>
                    )}
                    {phase === "select-quantity" ? (
                        pendingCount > 0 ? (
                            <button
                                type="button"
                                className="pallet-receiving-mode-switch"
                                onClick={() => setPhase("awaiting-pallet")}
                                title={t("scan_to_place")}
                            >
                                <MapPin size={16} />
                                <span>{t("scan_to_place")}</span>
                            </button>
                        ) : (
                            <span className="book-in-stat book-in-stat--count">
                                {t("pallets_today_count", { count: String(todayCount) })}
                            </span>
                        )
                    ) : (
                        <button
                            type="button"
                            className="pallet-receiving-mode-switch"
                            onClick={() => { setCurrentPallet(null); setPhase("select-quantity"); }}
                            title={t("print_labels")}
                        >
                            <Printer size={16} />
                            <span>{t("print_labels")}</span>
                        </button>
                    )}
                </div>
            )}

            {initialised && (
                <div className="scan-centre">
                    <div className="scan-command-wrap">
                        <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {commandWords.map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {initialised && phase === "select-quantity" && (
                <div className="pallet-receiving-quantity">
                    {/* Primary action sits directly under the command word
                        so it's the first thing the operator's eye lands
                        on after reading "How many labels?". The stepper
                        below is the secondary input that fine-tunes the
                        quantity before printing. */}
                    <button
                        type="button"
                        className={`pallet-receiving-print-btn ${isPrinting ? "pallet-receiving-print-btn--busy" : ""}`}
                        onClick={() => setPrintPromptOpen(true)}
                        disabled={isPrinting}
                    >
                        {isPrinting
                            ? <Loader2 size={28} className="pallet-receiving-spin" />
                            : <Printer size={28} />}
                        <span>{t("print_labels")}</span>
                    </button>
                    <div className="pallet-receiving-stepper">
                        <button
                            type="button"
                            className="pallet-receiving-step-btn"
                            onClick={() => setQuantity(q => Math.max(1, q - 1))}
                            disabled={quantity <= 1 || loading}
                            aria-label={t("decrease")}
                        >
                            <Minus size={28} />
                        </button>
                        <div className="pallet-receiving-step-value">{quantity}</div>
                        <button
                            type="button"
                            className="pallet-receiving-step-btn"
                            onClick={() => setQuantity(q => Math.min(QUANTITY_MAX, q + 1))}
                            disabled={quantity >= QUANTITY_MAX || loading}
                            aria-label={t("increase")}
                        >
                            <Plus size={28} />
                        </button>
                    </div>
                </div>
            )}

            {initialised && phase === "complete" && (
                <div className="pallet-receiving-complete">
                    <button
                        type="button"
                        className="pallet-receiving-print-btn"
                        onClick={startReceiveMore}
                    >
                        {t("receive_more")}
                    </button>
                </div>
            )}

            {/* Void list — shown only in remove mode. Mirrors the book-in
                items list styling (.book-in-items-list / .book-in-item-row)
                so operators get a familiar interaction. Each pending pallet
                can be voided either by scanning OR by tapping its X icon. */}
            {initialised && phase === "removing" && pendingList.length > 0 && (
                <div className="book-in-items-list">
                    {pendingList.map((p, idx) => (
                        <div key={p.pallet_id} className="book-in-item-row">
                            <span className="book-in-item-index">#{p.daily_seq}</span>
                            <span className="book-in-item-barcode">
                                {p.pallet_id}
                                {p.stock_type && <span className="pallet-receiving-type-tag">{p.stock_type}</span>}
                                {p.supplier_name && <span className="pallet-receiving-type-tag pallet-receiving-supplier-tag">{p.supplier_name}</span>}
                            </span>
                            <button
                                type="button"
                                className="pallet-receiving-row-void"
                                onClick={() => handleManualVoid(p.pallet_id)}
                                aria-label={t("void_label_mode")}
                                title={t("void_label_mode")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={loading || phase === "complete" || (phase === "select-quantity" && pendingCount === 0)}
                printConnected={printerOnline && !!printMode}
                onPrintClick={() => setShowPrinterSelect(true)}
            />

            {/* Print prompt — always shown on Print Labels. The supplier
                dropdown comes first (every active supplier, Amazon
                preselected); it is stored on each pallet and printed on
                the label, never in the barcode. With inbound stock types
                configured, one tap per type prints (the code prefixes
                every barcode in the batch); without, a single Print
                button does. */}
            {printPromptOpen && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={() => setPrintPromptOpen(false)}
                    data-testid="print-prompt"
                >
                    <div className="modal-card pallet-receiving-type-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("inbound_print_prompt", { count: String(quantity) })}</h2>
                            <button className="modal-close" onClick={() => setPrintPromptOpen(false)} aria-label={t("cancel")}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <label className="pallet-receiving-supplier-label">
                                <span>{t("inbound_supplier")}</span>
                                <select
                                    className="pallet-receiving-supplier-select"
                                    value={supplier}
                                    aria-label={t("inbound_supplier")}
                                    disabled={isPrinting}
                                    onChange={e => setSupplier(e.target.value)}
                                >
                                    {suppliers.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            </label>
                            {stockTypes.length > 0 ? (
                                <>
                                    <div className="pallet-receiving-type-heading">{t("inbound_stock_type_prompt")}</div>
                                    <div className="pallet-receiving-type-list">
                                        {stockTypes.map(st => (
                                            <button
                                                key={st.code}
                                                type="button"
                                                className="pallet-receiving-type-btn"
                                                disabled={isPrinting}
                                                onClick={() => handleGenerateAndPrint(st)}
                                            >
                                                <span>{st.name}</span>
                                                <span className="pallet-receiving-type-code">{st.code}</span>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="pallet-receiving-type-btn pallet-receiving-print-confirm"
                                    disabled={isPrinting}
                                    onClick={() => handleGenerateAndPrint()}
                                >
                                    <Printer size={22} />
                                    <span>{t("print_labels")}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Bin toggle — fixed bottom-LEFT, sits to the right of
                HiddenScanInput's printer-config icon. Voids printed-but-not-
                placed labels. Always available so the operator can react to
                a misprint at any phase. */}
            <div className="pallet-receiving-bin-toolbar">
                <button
                    type="button"
                    className={`scan-input-toggle-btn book-in-remove-btn ${phase === "removing" ? "book-in-remove-btn--active" : ""}`}
                    onClick={toggleRemoving}
                    disabled={loading || isPrinting}
                    aria-label={phase === "removing" ? t("exit_remove_mode") : t("void_label_mode")}
                    title={phase === "removing" ? t("exit_remove_mode") : t("void_label_mode")}
                >
                    {phase === "removing" ? <X size={20} /> : <Trash2 size={20} />}
                </button>
            </div>

            {/* Demo — toggled via Admin Settings → Demo Mode. Every button
                hits a real DB row: pending pallet IDs from /pending, loading-
                bay barcodes from /put-away/locations filtered by warehouse
                + type. */}
            {demo && initialised && (phase === "awaiting-pallet" || phase === "awaiting-location" || phase === "removing") && (
                <div className="book-in-demo-panel">
                    <span className="book-in-demo-label">Demo</span>
                    {(phase === "awaiting-pallet" || phase === "removing") && pendingList.slice(0, 4).map(p => (
                        <button
                            key={p.pallet_id}
                            className="book-in-demo-btn"
                            onClick={() => doSubmit(p.pallet_id)}
                        >
                            {p.pallet_id}
                        </button>
                    ))}
                    {phase === "awaiting-location" && (
                        <>
                            {demoLoadingBays.map(loc => (
                                <button key={loc} className="book-in-demo-btn" onClick={() => doSubmit(loc)}>{loc}</button>
                            ))}
                            {currentPallet && (
                                <button className="book-in-demo-btn" onClick={() => doSubmit(currentPallet)}>Cancel {currentPallet}</button>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Printer selection / install-Print-Agent prompt — opened from
                HiddenScanInput's bottom-left printer icon. Same modal the
                scansheet uses; handles Zebra agent install + network printer
                selection for the user's warehouse. */}
            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={printMode}
                    onSelect={() => {
                        setShowPrinterSelect(false);
                        setPrintMode("network");
                        setPrinterOnline(true);
                    }}
                    onConnectLocal={() => {
                        setShowPrinterSelect(false);
                        setPrintMode("local");
                        setPrinterOnline(true);
                    }}
                    onDisconnect={() => {
                        setShowPrinterSelect(false);
                        setPrintMode(null);
                        setPrinterOnline(false);
                    }}
                    onClose={() => {
                        setShowPrinterSelect(false);
                        // Re-detect — user may have just installed/connected.
                        detectPrinter();
                    }}
                />
            )}
        </div>
    );
};

export default PalletReceiving;
